import { z } from "zod";

/**
 * The entries AI analysis (user, 2026-10-31): per NOC per games, what the NOC
 * declared in its EOI / EBN against its last three SEA Games (the medal
 * history, `medal-history.ts`). ADMIN / SUPER_ADMIN only — the API's route
 * fence on the ACTIVE role; every other role 403.
 *
 * Consistency is the requirement: the same facts must always read the same.
 * So the analysis is FINDINGS, never prose. The API computes every finding
 * that is true by the rules below from our data alone; Claude only picks the
 * notable ones and orders them, and every number it cites is checked against
 * ours (a finding it cannot back is dropped). The GMS writes each finding
 * from ONE fixed template per kind (`entries` namespace, `analysis.finding.*`),
 * in the viewer's language. The result is stored with a fingerprint of the
 * exact input; it is regenerated only when that input changes.
 *
 * The kinds (a sport's "history" = the Games it was held in WITH a medal
 * table; "best" = its best single Games; "latest" = the latest of them):
 *   STRENGTH        won ≥ 1 gold in the sport over the history
 *   ABOVE_HISTORY   in the EOI, expects more (gold, or medals) than its best
 *   BELOW_HISTORY   in the EOI, expects fewer than at the latest Games (≥ 1 then)
 *   NOT_IN_EOI      won ≥ 1 gold in the sport, and the sport is not in the EOI
 *   NO_EBN_ENTRIES  won ≥ 1 gold, in the EOI, no EBN entry while the NOC has
 *                   EBN entries in other sports
 *   NO_PAST_MEDAL   in the EOI, held at least once, never a medal
 *   OVERALL         Σ expected gold over the EOI sports that give one, against
 *                   the gold won at the latest Games in every sport
 */

export const ENTRY_ANALYSIS_KINDS = ["STRENGTH", "ABOVE_HISTORY", "BELOW_HISTORY", "NOT_IN_EOI", "NO_EBN_ENTRIES", "NO_PAST_MEDAL", "OVERALL"] as const;
export type EntryAnalysisKind = (typeof ENTRY_ANALYSIS_KINDS)[number];

/** What an ABOVE / BELOW finding compares: expected gold, or expected medals (gold + silver + bronze, all three given). */
export const ENTRY_ANALYSIS_MEASURES = ["GOLD", "MEDALS"] as const;
export type EntryAnalysisMeasure = (typeof ENTRY_ANALYSIS_MEASURES)[number];

/** At most this many findings are kept, in the model's order. */
export const ENTRY_ANALYSIS_FINDINGS_MAX = 12;

/**
 * AVAILABLE, or why there is no analysis: a Para games (the history is the
 * SEA Games'), or an organisation that is not a NOC of the history.
 */
export const ENTRY_ANALYSIS_AVAILABILITY = ["AVAILABLE", "PARA_GAMES", "NOT_IN_HISTORY"] as const;
export type EntryAnalysisAvailability = (typeof ENTRY_ANALYSIS_AVAILABILITY)[number];

/**
 * `POST /entries/analysis/refresh`: UNCHANGED (the stored analysis already
 * matches the data), GENERATED (a new one was made and stored), IN_PROGRESS
 * (another request is making it — read again shortly).
 */
export const ENTRY_ANALYSIS_REFRESH_OUTCOMES = ["UNCHANGED", "GENERATED", "IN_PROGRESS"] as const;
export type EntryAnalysisRefreshOutcome = (typeof ENTRY_ANALYSIS_REFRESH_OUTCOMES)[number];

const uuid = z.string().uuid();

/** `GET /entries/analysis`, and the body of `POST /entries/analysis/refresh`. */
export const entryAnalysisQuery = z.object({ games_id: uuid, organization_id: uuid });
export type EntryAnalysisQuery = z.infer<typeof entryAnalysisQuery>;
export type EntryAnalysisQueryInput = z.input<typeof entryAnalysisQuery>;

export interface EntryAnalysisSport {
	sport_id: string;
	code: string;
	name: string;
}

/** Medals over the history in one sport. `games` = Games counted (held, with a medal table). */
interface HistoryCounts {
	gold: number;
	medals: number;
	games: number;
}

export type EntryAnalysisFinding =
	| ({ kind: "STRENGTH"; sport: EntryAnalysisSport; silver: number; bronze: number; rank: number } & HistoryCounts)
	| { kind: "ABOVE_HISTORY" | "BELOW_HISTORY"; sport: EntryAnalysisSport; measure: EntryAnalysisMeasure; expected: number; history: number; year: number }
	| ({ kind: "NOT_IN_EOI" | "NO_EBN_ENTRIES"; sport: EntryAnalysisSport } & HistoryCounts)
	| { kind: "NO_PAST_MEDAL"; sport: EntryAnalysisSport; games: number }
	| { kind: "OVERALL"; expected: number; history: number; year: number; sports: number };

export interface EntryAnalysisStored {
	findings: EntryAnalysisFinding[];
	generated_at: string;
	model: string;
}

/** `GET /entries/analysis` — the stored analysis (never generated by a GET). */
export interface EntryAnalysis {
	games_id: string;
	organization: { id: string; name: string; code: string | null };
	availability: EntryAnalysisAvailability;
	/** The last stored analysis; null before the first one. */
	analysis: EntryAnalysisStored | null;
	/** The EOI / EBN data changed since it was made (or there is none yet): the GMS asks for a refresh. */
	stale: boolean;
}

export interface EntryAnalysisRefresh extends EntryAnalysis {
	outcome: EntryAnalysisRefreshOutcome;
}
